import React from 'react';
import { View } from 'react-native';
import { fmtDate, fmtTime, type ApprovalStep, type WorkflowInstance } from '@hrms/shared';
import { humanize } from '../lib/date';
import { Badge } from './Badge';
import { Icon, type IconName } from './Icon';
import { Text } from './Text';
import { radius, toneColors, useTheme, type Tone } from './theme';

const stepTone = (s: ApprovalStep['status']): Tone => (s === 'approved' ? 'success' : s === 'rejected' ? 'danger' : s === 'pending' ? 'warning' : s === 'escalated' ? 'info' : 'neutral');
const stepIcon = (s: ApprovalStep['status']): IconName => (s === 'approved' ? 'check' : s === 'rejected' ? 'x' : s === 'pending' ? 'clock' : 'more');

function Item({ tone, icon, last, children }: { tone: Tone; icon: IconName; last?: boolean; children: React.ReactNode }) {
  const { colors } = useTheme();
  const t = toneColors(colors, tone);
  return (
    <View style={{ flexDirection: 'row', gap: 10 }}>
      <View style={{ alignItems: 'center' }}>
        <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: t.bg, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: colors.border }}>
          <Icon name={icon} size={12} color={t.fg} />
        </View>
        {!last && <View style={{ width: 2, flex: 1, backgroundColor: colors.border, marginVertical: 2 }} />}
      </View>
      <View style={{ flex: 1, paddingBottom: last ? 0 : 16, gap: 2 }}>{children}</View>
    </View>
  );
}

/** Vertical timeline of a WorkflowInstance's approval steps. */
export function ApprovalTimeline({ instance }: { instance: WorkflowInstance | null | undefined }) {
  const { colors } = useTheme();
  if (!instance) return null;
  const steps = [...(instance.steps ?? [])].sort((a, b) => a.step_index - b.step_index);
  return (
    <View>
      <Item tone="info" icon="send" last={!steps.length}>
        <Text weight="medium">Submitted{instance.requester ? ` by ${instance.requester.name}` : ''}</Text>
        <Text variant="small">
          {fmtDate(instance.created_at)} · {fmtTime(instance.created_at)}
        </Text>
      </Item>
      {steps.map((s, i) => (
        <Item key={s.id} tone={stepTone(s.status)} icon={stepIcon(s.status)} last={i === steps.length - 1}>
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Text weight="medium">{s.name}</Text>
            <Badge tone={stepTone(s.status)}>{humanize(s.status)}</Badge>
          </View>
          <Text variant="small">
            {s.approver?.name ?? 'Approver'}
            {s.acted_at ? ` · ${fmtDate(s.acted_at)} ${fmtTime(s.acted_at)}` : s.due_at ? ` · due ${fmtDate(s.due_at)}` : ''}
          </Text>
          {!!s.comment && (
            <View style={{ marginTop: 4, padding: 8, borderRadius: radius.md, backgroundColor: colors.surface2, borderWidth: 1, borderColor: colors.border }}>
              <Text size={13}>{s.comment}</Text>
            </View>
          )}
        </Item>
      ))}
    </View>
  );
}
